import { memo, ReactNode, useMemo } from 'react';
import Tooltip from '@creditflowresearch/uilib/Tooltip';
import { isScreenM } from '@creditflowresearch/uilib/utils/screen';
import ToggleButtonGroup from '@creditflowresearch/uilib/ToggleButtonGroup';
import LockedFeaturePopup from '@/creditflowresearch/ui/common/components/molecules/LockedFeaturePopup';

import styles from './togglingFilters.module.scss';

interface FilterItem {
  id: string,
  tooltip?: ReactNode,
  isActive: boolean,
  abbreviation: string,
  title?: string,
  hasRestrictedAccess?: boolean,
  isDisabled?: boolean,
}

interface IFilterGroupProps {
  filterName: string,
  items: FilterItem[],
  isDisabled: boolean,
  isTooltipEnabled: boolean,
  applyFilter: (name: string, id: string) => void,
}

const FilterGroup = ({ filterName, items, isDisabled, isTooltipEnabled, applyFilter }: IFilterGroupProps): React.ReactElement => {
  const activeIds = useMemo(
    () => items.filter((item) => item.isActive).map((item) => item.id),
    [items],
  );

  const handleChange = (id: string): void => {
    if (isDisabled) return;
    applyFilter(filterName, id);
  };

  return (
    <ToggleButtonGroup
      value={ activeIds }
      onChange={ handleChange }
      size="S"
      dataTest="togglingFilters"
      fit={ { sm: 'full', md: 'inline', xl: 'inline' } }
    >
      { items.map((item) => (
        (item.isDisabled && item.hasRestrictedAccess) ? (
          <LockedFeaturePopup
            key={ item.id }
            name={ item.title }
            content={ item.tooltip }
            className={ styles.filterItemRestrictedContainer }
          >
            <ToggleButtonGroup.Item
              value={ item.id }
              disabled
              dataTest="togglingFilterApplyButton"
            >
              { item.abbreviation }
            </ToggleButtonGroup.Item>
          </LockedFeaturePopup>
        ) : (
          <Tooltip
            key={ item.id }
            content={ item.tooltip }
            disabled={ !isTooltipEnabled || isScreenM() }
          >
            <ToggleButtonGroup.Item
              value={ item.id }
              disabled={ isDisabled || item.isDisabled }
              dataTest="togglingFilterApplyButton"
            >
              { item.abbreviation }
            </ToggleButtonGroup.Item>
          </Tooltip>
        )
      )) }
    </ToggleButtonGroup>
  );
};

interface IProps {
  className?: string,
  activeClassName?: string,
  isDisabled: boolean,
  isTooltipEnabled?: boolean,
  applyFilter: (name: string, id: string) => any,
  filterSet: {
    [name: string]: {
      items: FilterItem[],
      isVisible?: boolean,
    },
  },
}

const TogglingFilters = ({
  filterSet,
  isDisabled = false,
  isTooltipEnabled = true,
  applyFilter,
}: IProps) => Object.keys(filterSet)
  .map((filterName) => {
    const filter = filterSet[filterName];

    if (filter?.isVisible === false) {
      return null;
    }

    return (
      <FilterGroup
        key={ filterName }
        filterName={ filterName }
        items={ filter.items }
        isDisabled={ isDisabled }
        isTooltipEnabled={ isTooltipEnabled }
        applyFilter={ applyFilter }
      />
    );
  });

export default memo(TogglingFilters);
